/**
 * **O Núcleo Atômico: a bomba, desenhada como se tivesse sido renderizada em 3D.**
 *
 * ## O que "3D pré-renderizado" quer dizer aqui, e o que NÃO quer
 *
 * Quer dizer que a ILUMINAÇÃO é assada na textura. Não há motor 3D, não há
 * segundo canvas, não há contexto WebGL novo e não há dependência de runtime -
 * o gate proíbe as quatro coisas, e o pet 3D deste projeto existe precisamente
 * para mostrar quando é que uma delas se justifica. Aqui não se justifica: a
 * bomba é um objeto pequeno, numeroso quando a cadência sobe, e o que ela
 * precisa de comunicar é uma silhueta e um símbolo.
 *
 * O volume vem da mesma matemática que um renderizador usaria, avaliada UMA VEZ
 * ao gerar a textura em vez de por quadro:
 *
 *     N . L  para um cilindro de eixo horizontal
 *
 * A linha `dy` da textura é o paralelo `k = dy / R` do cilindro. A normal aí é
 * `(0, k, sqrt(1 - k²))`, a luz vem de cima e ligeiramente da frente, e o
 * produto interno dá exatamente a rampa que faz um retângulo ler-se como um
 * tubo. A ogiva à frente é a mesma conta com o perfil a fechar.
 *
 * ## As camadas, e a ordem
 *
 * 1. **aletas** - primeiro, para o corpo as tapar parcialmente e elas ficarem
 *    ATRÁS, que é o que dá profundidade;
 * 2. **corpo** - linha a linha, cada uma com o seu tom;
 * 3. **anéis de solda** - duas faixas escuras, para a escala se ler;
 * 4. **símbolo** - o trefólio amarelo;
 * 5. **sombreado do símbolo** - três faixas translúcidas por cima dele, para
 *    ele curvar com o casco em vez de flutuar colado.
 *
 * ## A rolagem, e porque OSCILA em vez de girar
 *
 * O contrato pede "rolagem/rotação 3D discreta, **sem tornar o símbolo
 * ilegível**". Uma revolução completa põe o símbolo de perfil - largura zero -
 * e depois esconde-o atrás do casco durante metade do ciclo. Então ela oscila:
 * o símbolo percorre ±42 % da volta e volta, encurtando em largura pelo cosseno
 * como um decalque num cilindro real, e nunca desaparece.
 *
 * Oito quadros, gerados uma vez e guardados no gestor de texturas. A escolha do
 * quadro é uma função pura do tempo decorrido - {@link atomicBombRollFrame} - o
 * que a torna testável sem um renderizador e igual em todos os clientes que
 * partilhem o mesmo relógio de apresentação.
 *
 * ## O que este ficheiro nunca sabe
 *
 * Que joia existe, que nível ela tem, quantas cópias, quanto dano faz, quem
 * disparou e quem levou. Ele recebe uma cena e um índice de quadro, e devolve
 * uma chave de textura.
 */

/**
 * A superfície mínima de desenho que este módulo precisa.
 *
 * Estrutural, como a de `projectile-art.ts` e pela mesma razão: `Phaser.Scene`
 * satisfaz isto sem que este ficheiro importe Phaser, e a aritmética da arte
 * fica testável fora de um runtime WebGL.
 */
export interface AtomicGraphics {
  fillStyle(color: number, alpha?: number): AtomicGraphics;
  fillRect(x: number, y: number, width: number, height: number): AtomicGraphics;
  fillCircle(x: number, y: number, radius: number): AtomicGraphics;
  fillEllipse(x: number, y: number, width: number, height: number): AtomicGraphics;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): AtomicGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface AtomicTextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): AtomicGraphics;
  };
}

/* -------------------------------------------------------------------------- */
/*  A geometria, em pixels da textura                                         */
/* -------------------------------------------------------------------------- */

/*
 * **A silhueta ROBUSTA, e o que ela deixou de ser** (revisão visual V28G.J).
 *
 * A primeira versão era fina: 113 px de vão contra 30 de corpo, uma razão de
 * 3,8:1 que lida como míssil. A avaliação manual pediu **+45 % de comprimento e
 * +80 % de largura**, e é isso que estes números são - 164 de vão contra 54 de
 * corpo, uma razão de 3,0:1.
 *
 * O que essa razão faz, e é o ponto inteiro: a bomba deixa de poder ser
 * confundida com um tiro ampliado. Um traço de tiro é 34x5 - sete para um - e
 * nenhuma escala transforma um no outro.
 *
 * A ogiva encurtou em PROPORÇÃO: era 26 % do vão, é 20 %. Um bico curto sobre um
 * corpo grosso é o que separa "bomba" de "torpedo", e é a leitura da Little Boy
 * sem lhe copiar detalhe nenhum.
 */

/**
 * Largura da textura da bomba, em pixels.
 *
 * Escolhida para a silhueta ficar CENTRADA na moldura: ela vive em 10..174, e
 * 184 dá-lhe dez pixels de margem de cada lado. Importa porque o sprite se
 * desenha com origem 0,5 e é o centro dele que a simulação diz onde está - uma
 * moldura assimétrica desviaria o casco do ponto onde ele detona.
 */
export const ATOMIC_BOMB_TEXTURE_WIDTH = 184;
/** Altura da textura, com folga para as aletas saírem do corpo. */
export const ATOMIC_BOMB_TEXTURE_HEIGHT = 100;

/** Meia-altura do corpo cilíndrico. Era 15; +80 % dá 27. */
const BODY_HALF = 27;
/** Onde a ogiva começa e onde ela encontra o cilindro. */
const NOSE_TIP_X = 10;
const NOSE_END_X = 42;
/** Onde o cilindro começa a afunilar para a cauda, e onde ele acaba. */
const TAIL_START_X = 138;
const TAIL_END_X = 160;
/**
 * Meia-altura no fim da cauda, como fração de {@link BODY_HALF}.
 *
 * Era 0,5 e passou a 0,62: a traseira aperta MENOS, que é o que "traseira
 * robusta" quer dizer. Uma cauda que afunila a metade lê-se como a ponta de um
 * torpedo.
 */
const TAIL_PINCH = 0.62;

/** O eixo do corpo, na vertical da textura. */
const AXIS_Y = ATOMIC_BOMB_TEXTURE_HEIGHT / 2;

/**
 * O centro do corpo, e onde o símbolo vive quando a rolagem está a zero.
 *
 * No meio da secção CILÍNDRICA - `(42 + 138) / 2 = 90` - e dois pixels à frente
 * dele. Com a rolagem a deslocá-lo `+/- SYMBOL_TRAVEL` e o cosseno a encurtá-lo,
 * a sua borda percorre 60..124 e nunca sai do cilindro (42..138), que é a
 * condição para continuar legível: um decalque que trepasse a ogiva ficaria
 * deformado pela curva dela.
 */
const SYMBOL_CENTRE_X = 92;
/** Era 11 sobre um corpo de 15; é 20 sobre um de 27 - maior em absoluto e em proporção. */
const SYMBOL_RADIUS = 20;
/**
 * Quanto o decalque anda ao longo do casco, do centro para cada lado.
 *
 * Nomeado porque duas coisas dependem dele: o desenho, que o aplica, e a prova,
 * que verifica que o curso inteiro cabe no cilindro. Escrito duas vezes, seria
 * uma prova a garantir um percurso que o desenho já não faz.
 */
const SYMBOL_TRAVEL = BODY_HALF * 1.05;

/** O grafite de que a bomba é feita, antes da luz. */
const HULL_COLOUR = 0x24262b;
/** O amarelo do símbolo, e o escuro das suas lâminas. */
const SYMBOL_YELLOW = 0xf2c518;
const SYMBOL_DARK = 0x14151a;

/*
 * **As aletas, e porque elas têm dois números só para si.**
 *
 * O contrato pediu "estabilizadores legíveis". Legível, a esta escala, quer
 * dizer que a aleta AVANÇA para lá da cauda e SOBE para lá do corpo o
 * suficiente para sobreviver ao zoom mínimo - a bomba desenha-se com uns 40
 * unidades de mundo de comprimento, e um detalhe que ocupe 2 % dela desaparece.
 *
 * Estão aqui em cima, e não enterrados no desenho, porque duas coisas os leem: o
 * desenho das aletas e o vão exportado. Um número repetido nos dois sítios era
 * uma silhueta que crescia sem o vão crescer com ela, e um chamador a escalar
 * pelo comprimento errado.
 */
/** Quanto a ponta da aleta passa para lá do fim da cauda, em pixels. */
const FIN_OVERHANG = 14;
/** Até onde a aleta sobe, em múltiplos de {@link BODY_HALF}. */
const FIN_SPAN_FACTOR = 1.62;

/**
 * **O comprimento DESENHADO da bomba dentro da textura**, em pixels.
 *
 * Do bico à ponta das aletas. Existe para o chamador poder escalar por
 * COMPRIMENTO em vez de pela largura da moldura: a moldura tem margem para as
 * aletas saírem, e escalar por ela faria a bomba ficar sistematicamente mais
 * curta do que o número pedido.
 *
 * O contrato do §3 pede um corpo maior do que uma bala e MENOR do que a caixa de
 * proximidade, e é isto que o chamador usa para o garantir.
 */
export const ATOMIC_BOMB_BODY_SPAN = TAIL_END_X + FIN_OVERHANG - NOSE_TIP_X;

/**
 * **A silhueta, em números que uma prova consegue afirmar.**
 *
 * A arte em si é pixels e não se afirma; as PROPORÇÕES afirmam-se, e são elas
 * que o contrato visual nomeia - "+45 % de comprimento", "+80 % de largura",
 * "nariz relativamente curto", "traseira robusta". Sem isto uma prova teria de
 * ler a textura, o que exige uma tela, ou de repetir os números, o que a faria
 * concordar consigo mesma em vez de com o desenho.
 *
 * `halfHeight` inclui as ALETAS de propósito: a caixa de proximidade tem de
 * conter a silhueta inteira, e a parte mais alta da bomba não é o corpo.
 */
export const ATOMIC_BOMB_SILHOUETTE = Object.freeze({
  /** Do bico à ponta das aletas. O mesmo número que {@link ATOMIC_BOMB_BODY_SPAN}. */
  span: TAIL_END_X + FIN_OVERHANG - NOSE_TIP_X,
  /** Meia-altura do cilindro, sem aletas. */
  bodyHalf: BODY_HALF,
  /** Meia-altura máxima, aletas incluídas. */
  halfHeight: BODY_HALF * FIN_SPAN_FACTOR,
  /** Comprimento da ogiva, do bico ao ombro. */
  noseLength: NOSE_END_X - NOSE_TIP_X,
  /** Quanto a cauda aperta: 1 seria um corte reto, 0 seria um bico. */
  tailPinch: TAIL_PINCH,
  /** Raio do decalque radioativo. */
  symbolRadius: SYMBOL_RADIUS,
  /** Onde o cilindro começa e acaba, relativo ao bico. */
  cylinderFrom: NOSE_END_X - NOSE_TIP_X,
  cylinderTo: TAIL_START_X - NOSE_TIP_X,
  /** O centro do decalque e o seu curso de rolagem, relativos ao bico. */
  symbolCentre: SYMBOL_CENTRE_X - NOSE_TIP_X,
  symbolTravel: SYMBOL_TRAVEL,
});

/** Quantos quadros de rolagem existem. Oito chegam e não são caros. */
export const ATOMIC_BOMB_ROLL_FRAMES = 8;

/** Quanto da volta o símbolo percorre, em cada sentido. Nunca meia volta. */
const ROLL_SWING = 0.42;

/** Duração de um ciclo completo de rolagem, em ms. */
export const ATOMIC_BOMB_ROLL_PERIOD_MS = 900;

/**
 * O quadro de rolagem para um instante, como função PURA do tempo.
 *
 * Uma onda triangular sobre {@link ATOMIC_BOMB_ROLL_FRAMES}: o símbolo vai para
 * um lado, volta, e vai para o outro. Determinística e sem estado, portanto duas
 * cenas com o mesmo relógio desenham o mesmo quadro - e um teste pode
 * verificá-la sem abrir um canvas.
 *
 * Nunca decide mecânica: rotação de sprite não toca em dano, colisão, raio nem
 * empurrão.
 */
export function atomicBombRollFrame(elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs)) {
    return 0;
  }
  const period = ATOMIC_BOMB_ROLL_PERIOD_MS;
  const phase = (((elapsedMs % period) + period) % period) / period;
  const index = Math.floor(phase * ATOMIC_BOMB_ROLL_FRAMES);
  return index >= ATOMIC_BOMB_ROLL_FRAMES ? ATOMIC_BOMB_ROLL_FRAMES - 1 : index;
}

/**
 * A fase de rolagem de um quadro, em voltas, dentro de `[-ROLL_SWING, ROLL_SWING]`.
 *
 * Uma onda triangular no ÍNDICE, para o ciclo fechar sem salto: o quadro 0 e o
 * quadro 7 são vizinhos no tempo, portanto têm de ser vizinhos na fase.
 */
export function atomicBombRollPhase(frame: number): number {
  const t = ((frame % ATOMIC_BOMB_ROLL_FRAMES) + ATOMIC_BOMB_ROLL_FRAMES) % ATOMIC_BOMB_ROLL_FRAMES;
  const half = ATOMIC_BOMB_ROLL_FRAMES / 2;
  /* 0..half sobe de -1 para +1; half..fim desce de volta. */
  const triangle = t <= half ? (t / half) * 2 - 1 : 3 - (t / half) * 2;
  return triangle * ROLL_SWING;
}

/* -------------------------------------------------------------------------- */
/*  A luz                                                                     */
/* -------------------------------------------------------------------------- */

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function mix(from: number, to: number, amount: number): number {
  const t = clamp01(amount);
  const fr = (from >> 16) & 0xff;
  const fg = (from >> 8) & 0xff;
  const fb = from & 0xff;
  const tr = (to >> 16) & 0xff;
  const tg = (to >> 8) & 0xff;
  const tb = to & 0xff;
  const r = Math.round(fr + (tr - fr) * t);
  const g = Math.round(fg + (tg - fg) * t);
  const b = Math.round(fb + (tb - fb) * t);
  return (r << 16) | (g << 8) | b;
}

function scaleColour(colour: number, factor: number): number {
  const r = Math.min(255, Math.round(((colour >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((colour >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((colour & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

/**
 * **A luz sobre um paralelo do cilindro.** `k` é `dy / R`, em `[-1, 1]`.
 *
 * `N . L` com a normal `(0, k, sqrt(1 - k²))` e a luz vinda de cima e um pouco
 * da frente. Mais um realce especular estreito perto do topo, que é o que faz
 * metal parecer metal em vez de plástico.
 *
 * Exportada porque é a única parte desta arte que um teste consegue afirmar sem
 * ler pixels: a rampa tem de ser monótona do topo para o meio, e o fundo tem de
 * ficar acima de zero (a luz ambiente) em vez de a preto puro.
 */
export function atomicHullShade(k: number): number {
  const depth = Math.sqrt(Math.max(0, 1 - k * k));
  const lambert = Math.max(0, 0.66 * depth - 0.75 * k);
  const ambient = 0.17;
  const specular = Math.exp(-(((k + 0.68) / 0.17) ** 2)) * 0.9;
  return clamp01(ambient + lambert * 0.95) + specular;
}

/** A meia-altura do corpo em `x`, seguindo a ogiva à frente e o afunilamento atrás. */
function halfHeightAt(x: number): number {
  if (x <= NOSE_TIP_X || x >= TAIL_END_X) {
    return 0;
  }
  if (x < NOSE_END_X) {
    /* Ogiva: um quarto de elipse do bico até ao ombro. */
    const t = (NOSE_END_X - x) / (NOSE_END_X - NOSE_TIP_X);
    return BODY_HALF * Math.sqrt(Math.max(0, 1 - t * t));
  }
  if (x > TAIL_START_X) {
    const t = (x - TAIL_START_X) / (TAIL_END_X - TAIL_START_X);
    return BODY_HALF * (1 - t * (1 - TAIL_PINCH));
  }
  return BODY_HALF;
}

/* -------------------------------------------------------------------------- */
/*  A bomba                                                                   */
/* -------------------------------------------------------------------------- */

function bombTextureKey(frame: number): string {
  return `atomic-bomb-roll-${frame}`;
}

/**
 * A textura da bomba para um quadro de rolagem, criada uma vez e reutilizada.
 *
 * O comprimento fica ao longo de `+x` e a espessura ao longo de `y`, para o
 * chamador poder rodar o sprite para a direção da velocidade - a mesma convenção
 * do traço do tiro comum, para que uma cena não tenha duas regras.
 */
/**
 * **O espelho que põe o bico à frente.**
 *
 * O desenho acima é autorado com o bico à ESQUERDA - é como se desenha uma
 * bomba de perfil, e é a orientação em que a silhueta foi aprovada. Mas um
 * projétil desenha-se com `setRotation(heading)`, e o `heading` é um ângulo de
 * mundo onde zero aponta para a DIREITA. Um sprite autorado com o bico à
 * esquerda voa, literalmente, de calda à frente. Foi o que a avaliação manual
 * viu.
 *
 * A convenção do repositório é a do traço - `DASH_ORIGIN_X` fica "just behind
 * the tip", a 0,94 da largura - portanto o bico vive no +x e é essa a regra que
 * a bomba tinha de cumprir.
 *
 * Isto é um adaptador e não uma reescrita da geometria, porque a silhueta foi
 * APROVADA como está: reescrever quarenta coordenadas para inverter uma seta é
 * arriscar a forma para obter uma coisa que uma subtração dá. Cada chamada de
 * preenchimento passa por aqui e sai com o seu `x` refletido em
 * {@link ATOMIC_BOMB_TEXTURE_WIDTH}; o `y` NUNCA é tocado, e é isso que preserva
 * a luz - o sombreado do casco é função da latitude, portanto refletir na
 * horizontal não lhe mexe. O realce continua em cima.
 *
 * Um retângulo reflete-se pela sua borda ESQUERDA menos a largura, porque
 * `fillRect` recebe um canto e não um centro. Uma elipse e um triângulo
 * recebem centros e vértices, e refletem-se ponto a ponto.
 */
function mirroredGraphics(g: AtomicGraphics): AtomicGraphics {
  const flip = (x: number) => ATOMIC_BOMB_TEXTURE_WIDTH - x;
  const mirror: AtomicGraphics = {
    fillStyle(colour, alpha) {
      g.fillStyle(colour, alpha);
      return mirror;
    },
    fillRect(x, y, width, height) {
      g.fillRect(flip(x) - width, y, width, height);
      return mirror;
    },
    fillCircle(x, y, radius) {
      g.fillCircle(flip(x), y, radius);
      return mirror;
    },
    fillEllipse(x, y, width, height) {
      g.fillEllipse(flip(x), y, width, height);
      return mirror;
    },
    fillTriangle(x0, y0, x1, y1, x2, y2) {
      g.fillTriangle(flip(x0), y0, flip(x1), y1, flip(x2), y2);
      return mirror;
    },
    generateTexture(key, width, height) {
      return g.generateTexture(key, width, height);
    },
    destroy() {
      g.destroy();
    },
  };
  return mirror;
}

export function ensureAtomicBombTexture(scene: AtomicTextureScene, frame: number): string {
  const index =
    ((Math.trunc(frame) % ATOMIC_BOMB_ROLL_FRAMES) + ATOMIC_BOMB_ROLL_FRAMES) %
    ATOMIC_BOMB_ROLL_FRAMES;
  const key = bombTextureKey(index);
  if (scene.textures.exists(key)) {
    return key;
  }
  /*
   * Refletida ao ser desenhada, e não ao ser usada.
   *
   * A alternativa era `setFlipX` em cada cena. Seriam dois sítios a ter de
   * concordar, e um terceiro consumidor futuro desenharia a bomba ao contrário
   * sem nada o avisar - a orientação de uma textura é propriedade da textura.
   */
  const g = mirroredGraphics(scene.make.graphics({ x: 0, y: 0 }, false));
  drawFins(g);
  drawBody(g);
  drawWeldRings(g);
  drawSymbol(g, atomicBombRollPhase(index));
  g.generateTexture(key, ATOMIC_BOMB_TEXTURE_WIDTH, ATOMIC_BOMB_TEXTURE_HEIGHT);
  g.destroy();
  return key;
}

/**
 * As aletas traseiras. **Legíveis**, que é o que a revisão pediu.
 *
 * Desenhadas ANTES do corpo, para ele lhes tapar a raiz: uma aleta que começa
 * atrás do casco lê-se como estando do outro lado dele, e é essa oclusão que dá
 * a profundidade que um contorno sozinho não dá.
 *
 * Quatro, e não três: duas de perfil (cima e baixo), a terceira sugerida por uma
 * lâmina no eixo - como uma aleta apontada ao observador aparece de lado - e uma
 * cinta a fechar a raiz das três, que é o que impede a traseira de se ler como
 * um afunilamento liso.
 *
 * A raiz arranca à frente de `TAIL_START_X` de propósito: uma aleta cuja raiz
 * coincide com o início do afunilamento parece colada, e não montada.
 */
function drawFins(g: AtomicGraphics): void {
  const root = TAIL_START_X - 10;
  const tip = TAIL_END_X + FIN_OVERHANG;
  const span = BODY_HALF * FIN_SPAN_FACTOR;
  /* A de cima apanha a luz; a de baixo fica na sombra. */
  g.fillStyle(scaleColour(HULL_COLOUR, 2.05), 1);
  g.fillTriangle(
    root,
    AXIS_Y - BODY_HALF * 0.72,
    tip,
    AXIS_Y - span,
    tip,
    AXIS_Y - BODY_HALF * 0.3,
  );
  g.fillStyle(scaleColour(HULL_COLOUR, 0.85), 1);
  g.fillTriangle(
    root,
    AXIS_Y + BODY_HALF * 0.72,
    tip,
    AXIS_Y + span,
    tip,
    AXIS_Y + BODY_HALF * 0.3,
  );
  /* A terceira, de perfil: uma lâmina no eixo, agora com espessura de casco. */
  g.fillStyle(scaleColour(HULL_COLOUR, 1.35), 1);
  g.fillRect(root + 4, AXIS_Y - 3, tip - root - 4, 6);
  /*
   * E a cinta que as segura, à volta da cauda.
   *
   * Um anel curto e claro na raiz: dá à traseira uma aresta de que a luz pega, e
   * é o que faz o conjunto ler-se como uma peça montada num corpo em vez de um
   * bico a terminar.
   */
  g.fillStyle(scaleColour(HULL_COLOUR, 1.5), 1);
  g.fillRect(root, AXIS_Y - BODY_HALF * 0.9, 5, BODY_HALF * 1.8);
}

/**
 * O corpo, uma linha de cada vez.
 *
 * Uma linha por pixel de altura, cada uma com a cor que a sua latitude no
 * cilindro merece. É a forma mais barata de assar um gradiente cilíndrico com
 * primitivas de preenchimento, e é exatamente o que um renderizador faria - só
 * que uma vez, e não sessenta vezes por segundo.
 */
function drawBody(g: AtomicGraphics): void {
  for (let dy = -BODY_HALF; dy <= BODY_HALF; dy += 1) {
    const k = dy / BODY_HALF;
    const shade = atomicHullShade(k);
    /* Grafite iluminado, e o realce puxado para branco em vez de para cinzento. */
    const base = scaleColour(HULL_COLOUR, Math.min(3.4, shade * 3.0));
    const colour = shade > 1 ? mix(base, 0xffffff, Math.min(0.55, (shade - 1) * 0.8)) : base;
    /*
     * A linha vai do bico ao fim da cauda, e o perfil decide onde ela começa e
     * acaba. Resolver `halfHeightAt(x) >= |dy|` analiticamente daria a mesma
     * coisa; procurar os dois extremos com um passo de um pixel é mais legível e
     * corre uma vez na vida do processo.
     */
    let start = -1;
    let end = -1;
    for (let x = NOSE_TIP_X; x <= TAIL_END_X; x += 1) {
      if (halfHeightAt(x) >= Math.abs(dy)) {
        if (start < 0) {
          start = x;
        }
        end = x;
      }
    }
    if (start < 0) {
      continue;
    }
    g.fillStyle(colour, 1);
    g.fillRect(start, AXIS_Y + dy, end - start + 1, 1);
  }
  /*
   * O ponto especular da OGIVA, que a rampa por linhas não produz.
   *
   * A rampa é constante ao longo de `x` porque um cilindro é; um bico curvo não
   * é, e sem isto a frente da bomba lê-se lisa. Uma elipse pequena e clara no
   * ombro superior resolve-o, e é o mesmo truque que um realce especular é.
   */
  g.fillStyle(0xffffff, 0.34);
  g.fillEllipse(NOSE_END_X - 8, AXIS_Y - BODY_HALF * 0.62, 22, 10);
  g.fillStyle(0xffffff, 0.16);
  g.fillEllipse(NOSE_END_X + 8, AXIS_Y - BODY_HALF * 0.72, 40, 7);
}

/**
 * Duas soldas escuras. Existem para a escala se ler, e para mais nada.
 *
 * Uma a fechar a ogiva e outra a abrir a cauda, e ambas FORA do disco do
 * símbolo (72..112): uma solda por cima do trefólio partia-o ao meio, que é
 * exatamente o detalhe que o torna ilegível ao longe.
 */
function drawWeldRings(g: AtomicGraphics): void {
  for (const x of [NOSE_END_X + 10, TAIL_START_X - 10]) {
    g.fillStyle(0x000000, 0.35);
    g.fillRect(x, AXIS_Y - halfHeightAt(x), 3, halfHeightAt(x) * 2);
    g.fillStyle(0xffffff, 0.1);
    g.fillRect(x + 3, AXIS_Y - halfHeightAt(x), 2, halfHeightAt(x) * 2);
  }
}

/**
 * **O trefólio radioativo, amarelo e legível.**
 *
 * Um disco amarelo com três lâminas escuras e um núcleo escuro - a leitura
 * negativa do símbolo, que é a que sobrevive a ser pequena. Cada lâmina é
 * aproximada por dois triângulos, o que a 40 px de largura é indistinguível de
 * um setor verdadeiro e não precisa de um caminho curvo.
 *
 * `phase` é a rolagem em voltas. Ela move o símbolo ao longo do casco e
 * ENCURTA-O em largura pelo cosseno, que é o que um decalque num cilindro faz.
 * Nunca o vira de perfil: `ROLL_SWING` para muito antes disso.
 */
function drawSymbol(g: AtomicGraphics, phase: number): void {
  const angle = phase * Math.PI * 2;
  const fore = Math.cos(angle);
  const cx = SYMBOL_CENTRE_X + Math.sin(angle) * SYMBOL_TRAVEL;
  const rx = SYMBOL_RADIUS * Math.abs(fore);
  const ry = SYMBOL_RADIUS;
  if (rx < 2) {
    return;
  }

  g.fillStyle(SYMBOL_YELLOW, 1);
  g.fillEllipse(cx, AXIS_Y, rx * 2, ry * 2);

  /* Três lâminas de 60 graus, a 120 graus umas das outras. */
  g.fillStyle(SYMBOL_DARK, 1);
  for (let blade = 0; blade < 3; blade += 1) {
    const centreAngle = (blade * 2 * Math.PI) / 3 - Math.PI / 2;
    const half = Math.PI / 6;
    const inner = 0.3;
    const outer = 1.02;
    const at = (a: number, r: number) => ({
      x: cx + Math.cos(a) * rx * r,
      y: AXIS_Y + Math.sin(a) * ry * r,
    });
    const a0 = at(centreAngle - half, outer);
    const a1 = at(centreAngle, outer);
    const a2 = at(centreAngle + half, outer);
    const hub = at(centreAngle, inner);
    g.fillTriangle(hub.x, hub.y, a0.x, a0.y, a1.x, a1.y);
    g.fillTriangle(hub.x, hub.y, a1.x, a1.y, a2.x, a2.y);
  }
  /* O núcleo escuro do símbolo. */
  g.fillStyle(SYMBOL_DARK, 1);
  g.fillEllipse(cx, AXIS_Y, rx * 0.62, ry * 0.62);

  /*
   * E o símbolo CURVA com o casco.
   *
   * Três faixas translúcidas por cima dele, com a mesma rampa do corpo: escura
   * em baixo, clara em cima. Sem isto o decalque flutua colado a uma superfície
   * plana, e é exatamente esse detalhe que separa "textura com um desenho" de
   * "objeto com um decalque".
   */
  for (let dy = -ry; dy <= ry; dy += 1) {
    const k = dy / ry;
    const shade = atomicHullShade(k);
    const width = rx * 2 * Math.sqrt(Math.max(0, 1 - k * k));
    if (width < 1) {
      continue;
    }
    if (shade < 0.75) {
      g.fillStyle(0x000000, Math.min(0.5, (0.75 - shade) * 0.8));
    } else if (shade > 1.05) {
      g.fillStyle(0xffffff, Math.min(0.35, (shade - 1.05) * 0.5));
    } else {
      continue;
    }
    g.fillRect(cx - width / 2, AXIS_Y + dy, width, 1);
  }
}

/* -------------------------------------------------------------------------- */
/*  A explosão                                                                */
/* -------------------------------------------------------------------------- */

const CORE_KEY = 'atomic-blast-core';
const FIREBALL_KEY = 'atomic-blast-fireball';
const SMOKE_KEY = 'atomic-blast-smoke';
const EMBER_KEY = 'atomic-blast-ember';

/**
 * O NÚCLEO branco-quente: um disco com queda muito abrupta.
 *
 * Branco puro no meio e quase nada na borda, para somar em `ADD` sem virar um
 * borrão. É a primeira coisa que aparece e a que dura menos.
 */
export function ensureAtomicCoreTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(CORE_KEY)) {
    return CORE_KEY;
  }
  const size = 128;
  const centre = size / 2;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let step = 48; step >= 0; step -= 1) {
    const t = step / 48;
    const alpha = (1 - t) ** 2.6;
    if (alpha < 0.01) {
      continue;
    }
    g.fillStyle(0xffffff, Math.min(1, alpha));
    g.fillCircle(centre, centre, centre * t + 1);
  }
  g.generateTexture(CORE_KEY, size, size);
  g.destroy();
  return CORE_KEY;
}

/**
 * A BOLA DE FOGO: branco no centro, amarelo, laranja, e escuro na borda.
 *
 * A cor está assada em vez de vir de um `setTint`, e a razão é a mesma que o
 * traço do tiro dá: tingir multiplicaria o centro quase branco até à cor da
 * borda e destruiria a estratificação, que é a única coisa que faz isto parecer
 * fogo em vez de um círculo cor de laranja.
 */
export function ensureAtomicFireballTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(FIREBALL_KEY)) {
    return FIREBALL_KEY;
  }
  const size = 160;
  const centre = size / 2;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let step = 60; step >= 0; step -= 1) {
    const t = step / 60;
    /* Branco -> amarelo -> laranja -> vermelho profundo, do meio para fora. */
    const colour =
      t < 0.28
        ? mix(0xfff6d8, 0xffd24a, t / 0.28)
        : t < 0.62
          ? mix(0xffd24a, 0xff7a1a, (t - 0.28) / 0.34)
          : mix(0xff7a1a, 0x8c1c05, (t - 0.62) / 0.38);
    const alpha = (1 - t) ** 1.5;
    if (alpha < 0.012) {
      continue;
    }
    g.fillStyle(colour, Math.min(1, alpha));
    g.fillCircle(centre, centre, centre * t + 1);
  }
  g.generateTexture(FIREBALL_KEY, size, size);
  g.destroy();
  return FIREBALL_KEY;
}

/**
 * A FUMAÇA escura: um disco cinzento-chumbo de bordas moles.
 *
 * `NORMAL` e nunca `ADD` - fumaça que soma luz é vapor. É a única camada aqui
 * que ESCURECE, e é o que dá peso a tudo o que veio antes.
 */
export function ensureAtomicSmokeTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(SMOKE_KEY)) {
    return SMOKE_KEY;
  }
  const size = 96;
  const centre = size / 2;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let step = 36; step >= 0; step -= 1) {
    const t = step / 36;
    const alpha = (1 - t) ** 1.9 * 0.62;
    if (alpha < 0.01) {
      continue;
    }
    g.fillStyle(mix(0x3a3630, 0x111014, t), alpha);
    g.fillCircle(centre, centre, centre * t + 1);
  }
  g.generateTexture(SMOKE_KEY, size, size);
  g.destroy();
  return SMOKE_KEY;
}

/** Um fragmento: uma lasca quente, curta e alongada. */
export function ensureAtomicEmberTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(EMBER_KEY)) {
    return EMBER_KEY;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(0xff9a2e, 0.55).fillEllipse(8, 4, 16, 6);
  g.fillStyle(0xffd98a, 1).fillEllipse(8, 4, 9, 3);
  g.fillStyle(0xfffaf0, 1).fillEllipse(9, 4, 4, 2);
  g.generateTexture(EMBER_KEY, 16, 8);
  g.destroy();
  return EMBER_KEY;
}

export {
  ATOMIC_BOMB_TEXTURE_HEIGHT as ATOMIC_BOMB_HEIGHT,
  CORE_KEY as ATOMIC_CORE_TEXTURE_KEY,
  EMBER_KEY as ATOMIC_EMBER_TEXTURE_KEY,
  FIREBALL_KEY as ATOMIC_FIREBALL_TEXTURE_KEY,
  SMOKE_KEY as ATOMIC_SMOKE_TEXTURE_KEY,
};
